"use client";

import React from "react";
import Link from "next/link";
import { businessData } from "@/data/business";
import { resolveMediaUrl } from "@/lib/resolve-media-url";

export interface SiteFooterProps {
  initialSettings?: Record<string, any> | null;
}

export function SiteFooter({ initialSettings }: SiteFooterProps = {}) {
  const contact = {
    ...businessData.contact,
    phone: initialSettings?.contactPhone || initialSettings?.phone || businessData.contact.phone,
    phoneRaw: initialSettings?.phoneRaw || businessData.contact.phoneRaw,
    whatsapp: initialSettings?.whatsapp_phone || businessData.contact.whatsapp,
    email: initialSettings?.contactEmail || businessData.contact.email,
    address: {
      ...businessData.contact.address,
      full: initialSettings?.address || businessData.contact.address.full,
    },
    social: {
      ...businessData.contact.social,
      ...(initialSettings?.socialLinks || []).reduce(
        (links: Record<string, string>, item: { platform?: string; href?: string }) => {
          if (item?.platform && item?.href) links[item.platform] = item.href;
          return links;
        },
        {} as Record<string, string>,
      ),
    },
  };
  const footerDescription = initialSettings?.footerDescription || businessData.brand.description;
  const copyrightText = initialSettings?.footerCopyrightText || `© ${new Date().getFullYear()} Shaaira Biocare Private Limited. All rights reserved.`;
  const ctaTitle = initialSettings?.footerCtaTitle || "Have a custom formula idea or batch inquiry?";
  const ctaDescription = initialSettings?.footerCtaDescription || "Share your intended product, benchmark, packaging direction, and target batch with our formulation team.";
  const logoImage = resolveMediaUrl(initialSettings?.logoImage, "/assets/logos/shaaira-biocare-logo-icon.svg");
  const logoText = initialSettings?.logoText || businessData.brand.name;
  const logoParts = String(logoText).trim().split(/\s+/);
  const workingHours = initialSettings?.workingHours || "Mon–Sat 9:00 AM – 6:00 PM";

  return (
    <footer className="site-footer">
      <div className="footer-cta-bar">
        <div className="container footer-cta-inner">
          <div className="footer-cta-text">
            <h3>{ctaTitle}</h3>
            <p>{ctaDescription}</p>
          </div>
          <div className="footer-cta-actions">
            <Link className="btn btn-primary" href="/contact">
              Start Your Project{" "}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </Link>
            <a className="btn btn-whatsapp" href={`https://wa.me/${contact.whatsapp}`} target="_blank" rel="noopener">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 21l1.65-3.8a9 9 0 1 1 3.4 2.9L3 21" />
                <path d="M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1a5 5 0 0 0 5 5h1a.5.5 0 0 0 0-1h-1a.5.5 0 0 0 0 1" />
              </svg>{" "}
              Chat on WhatsApp
            </a>
          </div>
        </div>
      </div>

      <div className="container footer-grid">
        <div className="footer-col footer-brand">
          <Link className="footer-brand-title" href="/" aria-label="Shaaira Biocare Home">
            <img src={logoImage} alt="" className="brand-mark-lg" width={64} height={64} />
            <div className="footer-brand-text">
              <strong>{logoParts[0] || "Shaaira"}</strong>
              <strong>{logoParts.slice(1).join(" ") || "Biocare"}</strong>
            </div>
          </Link>
          <p>{footerDescription}</p>
          <div className="footer-social-wrap">
            <span className="footer-social-label">Connect With Us</span>
            <div className="footer-social">
              <a className="social" href={contact.social.instagram} target="_blank" rel="noopener" aria-label="Instagram">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                </svg>
              </a>
              <a className="social" href={contact.social.linkedin} target="_blank" rel="noopener" aria-label="LinkedIn">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                  <rect x="2" y="9" width="4" height="12" />
                  <circle cx="4" cy="4" r="2" />
                </svg>
              </a>
              <a className="social" href={contact.social.facebook} target="_blank" rel="noopener" aria-label="Facebook">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                </svg>
              </a>
              <a className="social" href={contact.social.youtube} target="_blank" rel="noopener" aria-label="YouTube">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="3" y="6" width="18" height="12" rx="4" />
                  <path d="m10 9 5 3-5 3z" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        <div className="footer-col">
          <h3>Product Range</h3>
          <Link href="/skin-care">Advanced Skin Care</Link>
          <Link href="/hair-care">Scalp & Hair Therapeutics</Link>
          <Link href="/body-care">Body & Dermal Wellness</Link>
          <Link href="/baby-mom-care">Pediatric & Maternity Care</Link>
          <Link href="/men-range">Men's Grooming & Performance</Link>
          <Link href="/other-care">Suncare & Personal Hygiene</Link>
          <Link href="/kits">Curated Bundles & Travel Sets</Link>
        </div>

        <div className="footer-col">
          <h3>Capabilities</h3>
          <Link href="/manufacturing">Manufacturing Guides</Link>
          <Link href="/services">Private Label Mfg</Link>
          <Link href="/services">Contract Manufacturing</Link>
          <Link href="/services">Formulation & R&D</Link>
          <Link href="/services">Packaging & Filling</Link>
          <Link href="/services">Quality Assurance & QC</Link>
          <Link href="/process">How We Work</Link>
          <Link href="/faq">FAQ</Link>
        </div>

        <div className="footer-col">
          <h3>Factory & Direct Contact</h3>
          <a href={`tel:${contact.phoneRaw}`}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>{" "}
            {contact.phone}
          </a>
          <a href={`https://wa.me/${contact.whatsapp}`} target="_blank" rel="noopener">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="wa-icon" aria-hidden="true">
              <path d="M3 21l1.65-3.8a9 9 0 1 1 3.4 2.9L3 21" />
              <path d="M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1a5 5 0 0 0 5 5h1a.5.5 0 0 0 0-1h-1a.5.5 0 0 0 0 1" />
            </svg>{" "}
            Direct WhatsApp Chat
          </a>
          <a href={`mailto:${contact.email}`}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>{" "}
            {contact.email}
          </a>
          <a href={contact.mapsUrl} target="_blank" rel="noopener">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>{" "}
            {contact.address.full}
          </a>
          <p className="footer-hours">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>{" "}
            {workingHours}
          </p>
        </div>
      </div>

      <div className="footer-trust-bar">
        <div className="container trust-inner">
          <div className="trust-pill">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="8" r="7" />
              <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
            </svg>{" "}
            GMP-Aligned Facility
          </div>
          <div className="trust-pill">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <polyline points="9 12 11 14 15 10" />
            </svg>{" "}
            ISO 9001:2015 Standards
          </div>
          <div className="trust-pill">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" />
              <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
            </svg>{" "}
            Cruelty-Free Formulations
          </div>
          <div className="trust-pill">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
              <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
              <line x1="12" y1="22.08" x2="12" y2="12" />
            </svg>{" "}
            Low MOQ Flexible Batches
          </div>
          <div className="trust-pill">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="1" y="3" width="15" height="13" />
              <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
              <circle cx="5.5" cy="18.5" r="2.5" />
              <circle cx="18.5" cy="18.5" r="2.5" />
            </svg>{" "}
            On-Time Delivery
          </div>
        </div>
      </div>

      <div className="footer-bottom container">
        <p>{copyrightText}</p>
        <p>
          <Link href="/privacy-policy">Privacy Policy</Link> · <Link href="/terms">Terms of Service</Link> · <a href="/sitemap.xml">Sitemap</a>
        </p>
      </div>
    </footer>
  );
}
